"use client";

import React, { useState, useRef, useEffect } from "react";
import { Sun, Moon, Monitor, Check } from "lucide-react";
import { useTheme, type Theme } from "./index";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n/context";

interface Props {
  variant?: "floating" | "inline";
  size?: "sm" | "md";
  className?: string;
  menuPlacement?: "bottom-end" | "top-start";
}

export function ThemeToggle({
  variant = "inline",
  size = "md",
  className,
  menuPlacement = "bottom-end",
}: Props) {
  const { theme, resolved, setTheme, mounted } = useTheme();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const opts: {
    value: Theme;
    label: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
  }[] = [
    { value: "light", label: t("common.theme.light", "روشن"), icon: Sun },
    { value: "dark", label: t("common.theme.dark", "تاریک"), icon: Moon },
    { value: "system", label: t("common.theme.system", "سیستم"), icon: Monitor },
  ];

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const Icon = !mounted ? Monitor : resolved === "dark" ? Moon : Sun;

  return (
    <div ref={ref} className={cn("relative", className)} suppressHydrationWarning>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("common.theme.toggle", "تغییر تم")}
        className={cn(
          "inline-flex items-center justify-center w-10 h-10 rounded-full transition-all bg-grayScale-100 dark:bg-grayScale-700 text-grayScale-700 dark:text-grayScale-200 hover:bg-grayScale-200 dark:hover:bg-grayScale-600 border border-grayScale-200 dark:border-grayScale-600",
          size === "sm" ? "h-9 w-9" : "h-10 w-10",
          variant === "floating"
            ? "border-white/10 bg-white/10 text-grayScale-700 shadow-sm backdrop-blur-xl hover:bg-white/20"
            : open
              ? "border-primary-300 bg-white text-primary-main shadow-sm ring-4 ring-primary-main/10 dark:border-primary-main/40 dark:bg-grayScale-700 dark:text-primary-300 dark:ring-primary-main/20"
              : "border-grayScale-200 bg-white text-grayScale-500 shadow-sm hover:border-grayScale-300 hover:text-grayScale-800 dark:border-grayScale-600 dark:bg-grayScale-700 dark:text-grayScale-200 dark:hover:bg-grayScale-600",
        )}
      >
        <Icon size={size === "sm" ? 16 : 18} />
      </button>

      {open && (
        <div
          className={cn(
            "absolute z-50 min-w-[168px] rounded-xl border p-1.5",
            "animate-in fade-in zoom-in-95 duration-150",
            menuPlacement === "top-start"
              ? "bottom-full start-0 mb-2 origin-bottom slide-in-from-bottom-2"
              : "end-0 mt-2 origin-top slide-in-from-top-2",
            "border-grayScale-200 bg-white shadow-xl shadow-grayScale-900/10 dark:border-grayScale-700 dark:bg-grayScale-800 dark:shadow-black/40",
          )}
        >
          {opts.map((o) => {
            const active = mounted && theme === o.value;
            return (
              <button
                key={o.value}
                onClick={() => {
                  setTheme(o.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors",
                  active
                    ? "bg-primary-100 font-semibold text-primary-main dark:bg-primary-main/20"
                    : "font-medium text-grayScale-600 hover:bg-grayScale-100 hover:text-grayScale-900 dark:text-grayScale-200 dark:hover:bg-grayScale-700",
                )}
              >
                <o.icon size={16} />
                <span className="flex-1 text-start">{o.label}</span>
                {active && <Check size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ThemeToggle;
